import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AnswerNetVideoTPVStatusType, WelcomeChecklistStatusType } from 'modules/types';
import { formatPhoneNumber } from 'shared/utils/formatPhoneNumber';
import { PHONE_NUMBERS } from 'constants/phoneNumbers';
import {
  isWelcomeChecklistFailed,
  isWelcomeChecklistPassed,
  isWelcomeChecklistRequired,
} from 'modules/status/utils/getWelcomeChecklistStatus';
import {
  BodyText,
  BodyTextBold,
  ProjectQualificationCopyWrapper,
} from './ProjectQualification.styles';

type Props = {
  welcomeChecklistStatus: WelcomeChecklistStatusType;
  isCurrentMilestone: boolean;
  tpvStatus: AnswerNetVideoTPVStatusType | undefined;
  shouldShowTPVContent: boolean;
};
export function ProjectQualificationCopy({
  welcomeChecklistStatus,
  isCurrentMilestone,
  tpvStatus,
  shouldShowTPVContent,
}: Props) {
  const { t } = useTranslation();

  const installerSteps = t('projectQualification.installerSteps', {
    returnObjects: true,
  }) as unknown as string[];

  const installerStepsTpv =
    (t('projectQualification.installerStepsTpv', {
      returnObjects: true,
    }) as unknown as string[]) || [];

  const welcomeChecklistRequired = isWelcomeChecklistRequired(welcomeChecklistStatus!);
  const welcomeChecklistFail = isWelcomeChecklistFailed(welcomeChecklistStatus!);
  const welcomeChecklistPass = isWelcomeChecklistPassed(welcomeChecklistStatus!);

  let checklistStatus: React.ReactNode | null = null;
  let closingText = false;
  if (isCurrentMilestone && !shouldShowTPVContent) {
    if (welcomeChecklistRequired) {
      checklistStatus = (
        <BodyTextBold as="p">{t('projectQualification.completeWelcomeChecklist')}</BodyTextBold>
      );
      closingText = true;
    }

    if (welcomeChecklistPass) {
      checklistStatus = (
        <BodyText as="p">
          <BodyTextBold as="p">{t('projectQualification.welcomeChecklistCompleted')}</BodyTextBold>
        </BodyText>
      );
      closingText = true;
    }

    if (welcomeChecklistFail) {
      checklistStatus = (
        <BodyText as="p">
          <Trans
            i18nKey="projectQualification.welcomeChecklistFail"
            values={{ phoneNumber: formatPhoneNumber(PHONE_NUMBERS.CUSTOMER_SUPPORT) }}
            components={{
              strong: <BodyTextBold as="p" />,
            }}
          />
        </BodyText>
      );
    }
  }
  const tpvStatusVerified = tpvStatus === AnswerNetVideoTPVStatusType.VERIFIED;
  const tpvStatusPending =
    tpvStatus === AnswerNetVideoTPVStatusType.PENDING || tpvStatus === undefined;
  const tpvStatusFailed = tpvStatus === AnswerNetVideoTPVStatusType.FAILED;
  const useTpvInstallerSteps = shouldShowTPVContent && isCurrentMilestone && tpvStatusVerified;
  const useInstallerSteps =
    (!shouldShowTPVContent && !welcomeChecklistFail) ||
    (!shouldShowTPVContent && !isCurrentMilestone);
  let tpvStatusText: React.ReactNode | null = null;
  let bodyTextTpv: React.ReactNode | null = null;
  if (isCurrentMilestone && shouldShowTPVContent) {
    if (tpvStatusPending) {
      tpvStatusText = (
        <BodyTextBold as="p">{t('projectQualification.pendingTpvStatusHeader')}</BodyTextBold>
      );
      bodyTextTpv = <BodyText as="p">{t('projectQualification.pendingTpvStatusBody')}</BodyText>;
    } else if (tpvStatusVerified) {
      tpvStatusText = (
        <BodyTextBold as="p">{t('projectQualification.verifiedTpvStatusHeader')}</BodyTextBold>
      );
      bodyTextTpv = (
        <BodyText as="p">{t('projectQualification.installerWillGetSystemReadyTpv')}</BodyText>
      );
      closingText = true;
    } else if (tpvStatusFailed) {
      bodyTextTpv = (
        <BodyText as="p">
          <Trans
            i18nKey="projectQualification.failedTpvStatus"
            values={{ phoneNumber: formatPhoneNumber(PHONE_NUMBERS.CUSTOMER_SUPPORT) }}
            components={{
              strong: <BodyTextBold as="p" />,
            }}
          />
        </BodyText>
      );
    }
  }

  return (
    <ProjectQualificationCopyWrapper>
      <BodyText as="p">
        <span>{t('projectQualification.excitedToJoin')}</span>
      </BodyText>

      {checklistStatus}
      {tpvStatusText}

      {useInstallerSteps && (
        <BodyText as="p">{t('projectQualification.installerWillGetSystemReady')}</BodyText>
      )}
      {bodyTextTpv}
      {useInstallerSteps && (
        <ul>
          {Array.isArray(installerSteps) &&
            installerSteps.map((text) => (
              <li key={text}>
                <BodyText as="p">{text}</BodyText>
              </li>
            ))}
        </ul>
      )}

      {useTpvInstallerSteps && (
        <ul>
          {Array.isArray(installerStepsTpv) &&
            installerStepsTpv.map((text) => (
              <li key={text}>
                <BodyText as="p">{text}</BodyText>
              </li>
            ))}
        </ul>
      )}

      {closingText && <BodyText as="p">{t('projectQualification.welcomeToEverBright')}</BodyText>}
    </ProjectQualificationCopyWrapper>
  );
}

export default ProjectQualificationCopy;
